/**
 * Simulation step function — uses the 1D Navier-Stokes SIMPLE solver.
 *
 * The NS solver handles mass, momentum, and energy conservation for
 * water/steam pipe networks using IAPWS-97 properties.
 *
 * The old energy-balance lumped-parameter solver has been removed.
 * For steady-state BoP design-point analysis, use the constraint solver
 * in src/services/thermoService.ts (ThermoAnalysisView).
 */

import type { Node, Edge } from 'reactflow';
import type { SimulationState, CustomComponentDef, Site } from './types';
import { runNSStep, type NSStepResult } from './ns/integration/stepAdapter';
import { NSWorkerClient, type WorkerStepOutcome } from './ns/worker/workerClient';
import { customComponentExecutionBlock } from '../lib/sim/customComponents/customComponentPackage';

export interface StepResult {
  nodes: Node[];
  simState: Partial<SimulationState>;
}

// Persistent NS solver state across frames
let _nsSolverState: any = null;

// Lazily-created worker client, used ONLY on the worker-solver path (flag ON).
// Kept module-scoped so it persists across frames like _nsSolverState. Never
// constructed when the flag is OFF, so the default path never touches it.
let _workerClient: NSWorkerClient | null = null;

function customComponentErrors(nodes: readonly Node[]): string[] {
  return nodes
    .filter((node) => node.type === 'custom')
    .map((node) => {
      const block = customComponentExecutionBlock(
        node.id,
        typeof node.data?.label === 'string' ? node.data.label : node.id,
        node.data?.customComponentPackage,
      );
      return `[NS_CUSTOM_COMPONENT_NON_EXECUTABLE] ${node.id}.${block.field}: ${block.message}`;
    });
}

export function runSimulationStep(
  nodes: Node[],
  edges: Edge[],
  simState: SimulationState,
  site: Site | null,
  _customComponents: CustomComponentDef[],
  dt_input: number
): StepResult {
  if (nodes.length === 0) {
    _nsSolverState = null;
    return {
      nodes,
      simState: {
        isRunning: false,
        errors: ['Simulation has no components. Please add components to the canvas.'],
        warnings: [],
      },
    };
  }
  const customErrors = customComponentErrors(nodes);
  if (customErrors.length > 0) {
    _nsSolverState = null;
    return {
      nodes,
      simState: {
        isRunning: false,
        errors: customErrors,
        warnings: [],
      },
    };
  }

  // Reset NS state if topology changed (different number of nodes/edges)
  if (_nsSolverState && (_nsSolverState._nodeCount !== nodes.length || _nsSolverState._edgeCount !== edges.length)) {
    _nsSolverState = null;
  }

  try {
    const result = runNSStep(nodes, edges, simState, site, _nsSolverState, dt_input);
    _nsSolverState = result.nsSolverState;
    if (_nsSolverState) {
      (_nsSolverState as any)._nodeCount = nodes.length;
      (_nsSolverState as any)._edgeCount = edges.length;
    }
    return { nodes: result.nodes, simState: result.simState };
  } catch (e: any) {
    return {
      nodes,
      simState: {
        warnings: [`NS solver: ${e.message}`],
        errors: [],
      },
    };
  }
}

/**
 * One TRANSIENT step routed through the off-main-thread Web Worker solver.
 *
 * ⚠️ Reached ONLY when `simState.useWorkerSolver` is true. The OFF path
 * (`runSimulationStep`) is untouched and never imports/constructs the worker
 * client. This async variant:
 *   • shares the SAME module-level `_nsSolverState` + topology-reset logic as
 *     `runSimulationStep`, so switching the flag mid-session is seamless;
 *   • on the FIRST step (no built state) runs the synchronous main-thread
 *     `runNSStep` (which BUILDS the network) and seeds a fresh worker epoch;
 *   • on subsequent steps serializes the live state to the worker, awaits a
 *     bit-identical advanced state, and transparently falls back to `runNSStep`
 *     on any worker failure/timeout (never hangs the UI).
 *
 * Returns the same `StepResult` shape as `runSimulationStep` plus `ranOnWorker`
 * / `fallbackReason` telemetry the caller may surface or ignore.
 */
export async function runSimulationStepViaWorker(
  nodes: Node[],
  edges: Edge[],
  simState: SimulationState,
  site: Site | null,
  _customComponents: CustomComponentDef[],
  dt_input: number,
): Promise<StepResult & { ranOnWorker: boolean; fallbackReason?: string }> {
  if (nodes.length === 0) {
    _nsSolverState = null;
    _workerClient?.dispose();
    _workerClient = null;
    return {
      nodes,
      ranOnWorker: false,
      simState: {
        isRunning: false,
        errors: ['Simulation has no components. Please add components to the canvas.'],
        warnings: [],
      },
    };
  }
  const customErrors = customComponentErrors(nodes);
  if (customErrors.length > 0) {
    _nsSolverState = null;
    _workerClient?.dispose();
    _workerClient = null;
    return {
      nodes,
      ranOnWorker: false,
      fallbackReason: 'custom component package is non-executable',
      simState: {
        isRunning: false,
        errors: customErrors,
        warnings: [],
      },
    };
  }

  // Reset NS state if topology changed (identical rule to runSimulationStep).
  // A topology change also opens a new worker epoch so any in-flight step for
  // the old network is dropped by the protocol's staleness guard.
  if (
    _nsSolverState &&
    (_nsSolverState._nodeCount !== nodes.length || _nsSolverState._edgeCount !== edges.length)
  ) {
    _nsSolverState = null;
    _workerClient?.startEpoch();
  }

  if (!_workerClient) {
    _workerClient = new NSWorkerClient();
    _workerClient.startEpoch();
  }

  try {
    const outcome: WorkerStepOutcome = await _workerClient.step(
      nodes,
      edges,
      simState,
      site,
      _nsSolverState,
      dt_input,
    );
    _nsSolverState = outcome.nsSolverState;
    if (_nsSolverState) {
      _nsSolverState._nodeCount = nodes.length;
      _nsSolverState._edgeCount = edges.length;
    }
    return {
      nodes: outcome.nodes,
      simState: outcome.simState,
      ranOnWorker: outcome.ranOnWorker,
      fallbackReason: outcome.fallbackReason,
    };
  } catch (e: any) {
    // Defensive: NSWorkerClient.step never rejects, but if construction or an
    // unexpected throw escaped, do NOT hang — surface a warning and stop here.
    return {
      nodes,
      ranOnWorker: false,
      fallbackReason: `worker step threw: ${e?.message ?? e}`,
      simState: { warnings: [`NS solver: ${e?.message ?? e}`], errors: [] },
    };
  }
}

/** Reset the persistent NS solver state (called when loading a new cycle). */
export function resetNSSolverState(): void {
  _nsSolverState = null;
  _workerClient?.dispose();
  _workerClient = null;
}
